import React from 'react';
import { Box, Stack, Alert, Button, Paper, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ProductInformation,
  LanguageSelection,
  SourceContent,
  TranslateButton,
  TranslationResult,
} from '../components/translation';
import { useNewTranslation } from '../hooks/useNewTranslation';
import PageBreadcrumbs from '../components/Breadcrumbs';
import PageHeader from '../components/PageHeader';

const NewTranslation: React.FC = () => {
  const { t } = useTranslation();
  const { id: productIdFromRoute } = useParams();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId') || productIdFromRoute || '';
  const {
    formData,
    validation,
    loading,
    saving,
    error,
    availableTargets,
    isExistingProduct,
    sourceLanguageDisplay,
    targetLanguageDisplay,
    flowStatus,
    updateField,
    generateTranslation,
    saveTranslation,
    copyTranslation,
    cancel,
  } = useNewTranslation();

  const hasGeneratedTranslation = formData.translatedText.trim().length > 0;
  const isSaveFailure = flowStatus === 'error' && hasGeneratedTranslation;
  // Lock source text for existing products always; for new products lock after translation is generated
  const lockSource = isExistingProduct || hasGeneratedTranslation;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageBreadcrumbs
        items={
          isExistingProduct && formData.name
            ? [
                { label: t('nav.products'), path: '/products' },
                { label: formData.name, path: `/products/${productId}` },
                { label: t('products.add') },
              ]
            : [{ label: t('translation.newTitle') }]
        }
      />

      <PageHeader
        title={isExistingProduct ? t('products.add') : t('translation.newTitle')}
        subtitle={isExistingProduct ? t('translation.autoSaveSubtitleExisting') : t('translation.autoSaveSubtitleNew')}
      />

      {/* Top action bar — always visible */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
            <LanguageSelection
              targetLanguage={formData.targetLanguage}
              availableTargets={availableTargets}
              onLanguageChange={(value) => updateField('targetLanguage', value)}
              disabled={loading || saving}
            />
            <TranslateButton
              canTranslate={validation.canTranslate && (isExistingProduct || formData.name.trim().length > 0)}
              loading={loading}
              targetLanguageDisplay={targetLanguageDisplay}
              onTranslate={generateTranslation}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={cancel}
            disabled={saving}
            startIcon={<CancelIcon />}
          >
            {t('translation.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={saveTranslation}
            disabled={!validation.canSave || saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ minWidth: 140 }}
          >
            {saving ? t('translation.saving') : t('translation.save')}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            isSaveFailure ? (
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={saveTranslation}>
                {t('translation.retrySave')}
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {!isExistingProduct && (
        <Box sx={{ mb: 3 }}>
          <ProductInformation
            name={formData.name}
            sku={formData.sku}
            ean={formData.ean}
            onNameChange={(value) => updateField('name', value)}
            onSkuChange={(value) => updateField('sku', value)}
            onEanChange={(value) => updateField('ean', value)}
          />
        </Box>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        {!hasGeneratedTranslation && (
          <SourceContent
            sourceText={formData.sourceText}
            sourceLanguage={formData.sourceLanguage}
            sourceLanguageDisplay={sourceLanguageDisplay}
            isExistingProduct={isExistingProduct}
            onSourceChange={(value) => updateField('sourceText', value)}
            disabled={lockSource}
          />
        )}

        {hasGeneratedTranslation && (
          <TranslationResult
            translation={formData.translatedText}
            targetLanguage={formData.targetLanguage}
            targetLanguageDisplay={targetLanguageDisplay}
            loading={loading}
            onTranslationChange={(value) => updateField('translatedText', value)}
            onCopy={copyTranslation}
            disableClpAssignment
            referenceText={formData.sourceText}
            referenceLabel={t('statementXml.referenceSentenceLabelWithLanguage', { language: sourceLanguageDisplay })}
          />
        )}
      </Stack>

      {(flowStatus === 'generating' ||
        flowStatus === 'generated' ||
        flowStatus === 'dirty' ||
        flowStatus === 'saving' ||
        flowStatus === 'saved') && (
        <Alert severity={flowStatus === 'saved' ? 'success' : flowStatus === 'dirty' ? 'warning' : 'info'}>
          {flowStatus === 'generating' && t('translation.statusGenerating')}
          {flowStatus === 'generated' && t('translation.statusGenerated')}
          {flowStatus === 'dirty' && t('translation.statusDirty')}
          {flowStatus === 'saving' && t('translation.statusSaving')}
          {flowStatus === 'saved' && t('translation.statusSaved')}
        </Alert>
      )}
    </Box>
  );
};

export default NewTranslation;

